import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import useIsMobile from '../../hooks/useIsMobile';
import { useLandingLanguage } from '../../context/LandingLanguageContext';

/** Horizontal gap between logo block and trailing cluster (matches nav `gap`). */
const navOuterGap = (isMobile) => (isMobile ? 12 : 28);
/** Gap between language toggle and waitlist CTA inside the trailing cluster. */
const langCtaGap = (isMobile) => (isMobile ? 8 : 16);
/** Small buffer so we stack before a hard overflow / subpixel squeeze. */
const FIT_BUFFER_PX = 6;

function LogoBlock({ isMobile }) {
  const { t } = useLandingLanguage();
  return (
    <Link
      to="/"
      aria-label={t('nav.home')}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? '10px' : '12px',
        flexShrink: 0,
        minWidth: 0,
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      <motion.div
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.98 }}
        style={{
          width: isMobile ? '32px' : '36px',
          height: isMobile ? '32px' : '36px',
          borderRadius: isMobile ? '10px' : '12px',
          background: 'linear-gradient(135deg, #D4AF37 0%, #B8962E 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: isMobile ? '16px' : '18px',
          fontWeight: '800',
          color: '#0F0F0F',
          boxShadow: '0 4px 16px rgba(212,175,55,0.3)',
          flexShrink: 0,
        }}
      >
        S
      </motion.div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }}>
        <span
          style={{
            fontSize: isMobile ? 'clamp(15px, 4.2vw, 18px)' : '22px',
            fontWeight: '800',
            color: '#F5F1EA',
            letterSpacing: '-0.5px',
            fontFamily: 'Inter, sans-serif',
            whiteSpace: 'nowrap',
            lineHeight: 1,
          }}
        >
          SIMO PAY
        </span>
        <span
          style={{
            fontSize: isMobile ? '8px' : '10px',
            fontWeight: '800',
            color: '#F5F1EA',
            letterSpacing: '1px',
            fontFamily: 'Inter, sans-serif',
            whiteSpace: 'nowrap',
            textTransform: 'uppercase',
            lineHeight: 1,
          }}
        >
          {t('nav.logoTagline')}
        </span>
      </div>
    </Link>
  );
}

function HamburgerIcon({ open }) {
  if (open) {
    return (
      <span
        aria-hidden
        style={{
          fontSize: 22,
          fontWeight: 300,
          color: '#E8E4DC',
          lineHeight: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 18,
          height: 14,
        }}
      >
        ×
      </span>
    );
  }
  return (
    <span
      aria-hidden
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 4,
        width: 18,
        height: 14,
      }}
    >
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            display: 'block',
            width: 18,
            height: 2,
            borderRadius: 1,
            background: '#E8E4DC',
          }}
        />
      ))}
    </span>
  );
}

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const { locale, setLocale, t } = useLandingLanguage();
  const location = useLocation();

  const navRef = useRef(null);
  const [stackLang, setStackLang] = useState(false);
  const [mobileDrawerTopPx, setMobileDrawerTopPx] = useState(0);

  const measureStack = useCallback(() => {
    if (isMobile) {
      setStackLang(false);
      return;
    }
    const nav = navRef.current;
    if (!nav) return;
    setStackLang(nav.scrollWidth > nav.clientWidth + FIT_BUFFER_PX);
  }, [isMobile]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobile) setMobileMenuOpen(false);
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile || !mobileMenuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobile, mobileMenuOpen]);

  useEffect(() => {
    if (!isMobile || !mobileMenuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobile, mobileMenuOpen]);

  const syncMobileDrawerTop = useCallback(() => {
    const nav = navRef.current;
    if (!nav) return;
    setMobileDrawerTopPx(nav.getBoundingClientRect().bottom);
  }, []);

  useLayoutEffect(() => {
    if (!isMobile || !mobileMenuOpen) return undefined;
    syncMobileDrawerTop();
    const nav = navRef.current;
    const ro = nav ? new ResizeObserver(syncMobileDrawerTop) : null;
    if (nav && ro) ro.observe(nav);
    window.addEventListener('resize', syncMobileDrawerTop);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', syncMobileDrawerTop);
    };
  }, [isMobile, mobileMenuOpen, syncMobileDrawerTop, scrolled]);

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    measureStack();
    const raf = requestAnimationFrame(() => measureStack());
    const ro = new ResizeObserver(() => measureStack());
    ro.observe(nav);
    window.addEventListener('resize', measureStack);
    let cancelled = false;
    const onFonts = () => {
      if (!cancelled) measureStack();
    };
    if (document.fonts?.ready) {
      document.fonts.ready.then(onFonts).catch(() => {});
    }
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', measureStack);
    };
  }, [measureStack, locale, scrolled, isMobile]);

  const langToggle = (
    <div
      role="group"
      aria-label={t('nav.langToggle')}
      style={{
        flexShrink: 0,
        display: 'flex',
        background: 'rgba(0,0,0,0.48)',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: '100px',
        padding: isMobile ? '4px' : '3px',
        gap: '2px',
      }}
    >
      {['en', 'fr', 'ar'].map((code) => (
        <button
          key={code}
          type="button"
          onClick={() => setLocale(code)}
          aria-pressed={locale === code}
          style={{
            border: 'none',
            borderRadius: '100px',
            padding: isMobile ? '10px 14px' : '7px 14px',
            minHeight: isMobile ? '44px' : undefined,
            minWidth: isMobile ? '44px' : undefined,
            fontSize: '11px',
            fontWeight: '800',
            letterSpacing: '0.06em',
            cursor: 'pointer',
            fontFamily: 'Inter, sans-serif',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            background: locale === code ? 'rgba(212,175,55,0.22)' : 'transparent',
            color: locale === code ? '#F0D070' : '#8B93A7',
            textShadow: locale === code ? '0 0 20px rgba(212,175,55,0.55), 0 1px 0 rgba(0,0,0,0.35)' : 'none',
            boxShadow: locale === code ? '0 0 0 1px rgba(212,175,55,0.35)' : 'none',
            transition: 'color 0.2s, background 0.2s',
          }}
        >
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  );

  const makeAboutLink = (opts = {}) => {
    const { fullWidthMobile, onNavigate } = opts;
    const aboutGold = '#E6D491';
    const sparkleSize = isMobile ? 16 : 18;
    return (
      <Link
        to="/about"
        onClick={onNavigate}
        style={{
          textDecoration: 'none',
          padding: isMobile ? '10px 14px' : '6px 12px',
          borderRadius: '12px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: fullWidthMobile ? 'flex-start' : 'center',
          minHeight: isMobile ? '44px' : undefined,
          width: fullWidthMobile ? '100%' : undefined,
          boxSizing: 'border-box',
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: isMobile ? 'clamp(13px, 3.6vw, 15px)' : '15px',
            fontWeight: 800,
            fontFamily: 'Inter, sans-serif',
            whiteSpace: 'nowrap',
            letterSpacing: '-0.02em',
            color: aboutGold,
          }}
        >
          {t('nav.aboutUs')}
          <svg
            width={sparkleSize}
            height={sparkleSize}
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden
            style={{ flexShrink: 0, display: 'block', marginTop: '0px' }}
          >
            <path
              fill={aboutGold}
              d="M12 3.25L13.62 8.38L18.75 10L13.62 11.62L12 16.75L10.38 11.62L5.25 10L10.38 8.38L12 3.25z"
            />
          </svg>
        </span>
      </Link>
    );
  };

  const aboutLink = makeAboutLink();

  /** `mobileBar`: second row on small screens so the CTA is never clipped beside lang + menu. */
  const getWaitlistCta = (opts = {}) => {
    const { mobileBar } = opts;
    const mobileInlineCap = !mobileBar && isMobile;
    return (
      <a
        href="/#waitlist"
        style={{
          textDecoration: 'none',
          minWidth: 0,
          display: mobileBar ? 'block' : undefined,
          width: mobileBar ? '100%' : undefined,
          maxWidth: mobileInlineCap ? 'min(168px, 46vw)' : undefined,
          ...(stackLang && !isMobile && !mobileBar
            ? {
                display: 'flex',
                justifyContent: 'flex-end',
              }
            : {}),
        }}
      >
        <motion.button
          data-testid="navbar-waitlist"
          type="button"
          whileHover={{ scale: 1.03, y: -1 }}
          whileTap={{ scale: 0.97 }}
          style={{
            background: 'linear-gradient(135deg, #D4AF37 0%, #B8962E 100%)',
            color: '#0F0F0F',
            border: 'none',
            borderRadius: '100px',
            padding: isMobile ? '10px 12px' : '10px 24px',
            fontSize: isMobile ? 'clamp(10px, 2.8vw, 12px)' : '13px',
            fontWeight: '700',
            cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(212,175,55,0.25)',
            letterSpacing: '-0.2px',
            lineHeight: 1.25,
            width: isMobile ? '100%' : undefined,
            maxWidth: '100%',
            boxSizing: 'border-box',
            textAlign: 'center',
            whiteSpace: mobileBar ? 'nowrap' : isMobile ? 'normal' : 'nowrap',
            wordBreak: mobileBar ? 'normal' : isMobile ? 'break-word' : 'normal',
            minHeight: isMobile ? '44px' : undefined,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            ...(!isMobile ? { minWidth: '176px', textTransform: 'uppercase' } : { textTransform: 'uppercase' }),
          }}
        >
          {t('nav.joinWaitlist')}
        </motion.button>
      </a>
    );
  };

  const waitlistCta = getWaitlistCta();
  const waitlistCtaMobileBar = getWaitlistCta({ mobileBar: true });

  const navPadding = isMobile
    ? {
        paddingTop: 'max(10px, env(safe-area-inset-top))',
        paddingBottom: '10px',
        paddingLeft: 'clamp(12px, 4vw, 22px)',
        paddingRight: 'clamp(12px, 4vw, 22px)',
      }
    : {
        padding: '20px clamp(56px, 8vw, 120px)',
      };

  const mobileNav = (
    <>
      <div
        style={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          minWidth: 0,
        }}
      >
        <LogoBlock isMobile={isMobile} />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexShrink: 0,
            minWidth: 0,
          }}
        >
          {langToggle}
          <button
            type="button"
            aria-expanded={mobileMenuOpen}
            aria-controls="nav-mobile-drawer"
            aria-label={mobileMenuOpen ? t('nav.menuClose') : t('nav.menu')}
            onClick={() => setMobileMenuOpen((o) => !o)}
            style={{
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '12px',
              background: 'rgba(255,255,255,0.06)',
              minWidth: 44,
              minHeight: 44,
              padding: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <HamburgerIcon open={mobileMenuOpen} />
          </button>
        </div>
      </div>
      <div
        style={{
          width: '100%',
          minWidth: 0,
          marginTop: 10,
          boxSizing: 'border-box',
        }}
      >
        {waitlistCtaMobileBar}
      </div>
    </>
  );

  const desktopStackNav = (
    <>
      <div
        style={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          gap: `${navOuterGap(isMobile)}px`,
        }}
      >
        <LogoBlock isMobile={isMobile} />
        <div
          style={{
            flex: 1,
            minWidth: 0,
          }}
        />
        {waitlistCta}
      </div>
      <div
        style={{
          display: 'flex',
          width: '100%',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        {aboutLink}
        {langToggle}
      </div>
    </>
  );

  const desktopRowNav = (
    <>
      <LogoBlock isMobile={isMobile} />
      <div
        style={{
          flex: 1,
          minWidth: 0,
        }}
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: `${langCtaGap(isMobile)}px`,
          flexShrink: 0,
        }}
      >
        {aboutLink}
        {langToggle}
        {waitlistCta}
      </div>
    </>
  );

  return (
    <>
      <motion.nav
        ref={navRef}
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          width: '100%',
          maxWidth: '100vw',
          boxSizing: 'border-box',
          ...navPadding,
          display: 'flex',
          flexDirection: isMobile || stackLang ? 'column' : 'row',
          alignItems: isMobile || stackLang ? 'stretch' : 'center',
          gap: isMobile ? '4px' : stackLang ? '10px' : navOuterGap(isMobile),
          background: scrolled ? 'rgba(15, 15, 15, 0.92)' : 'transparent',
          backdropFilter: scrolled ? 'blur(24px)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(24px)' : 'none',
          borderBottom: '1px solid transparent',
          // Do not use `transition: all` here — it tweens `transform`/`opacity` and fights Framer Motion’s entrance animation.
          transition:
            'background 0.4s cubic-bezier(0.4, 0, 0.2, 1), backdrop-filter 0.4s cubic-bezier(0.4, 0, 0.2, 1), -webkit-backdrop-filter 0.4s cubic-bezier(0.4, 0, 0.2, 1), border-bottom-color 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          overflowX: 'hidden',
        }}
      >
        {isMobile ? mobileNav : stackLang ? desktopStackNav : desktopRowNav}
      </motion.nav>
      <AnimatePresence>
        {isMobile && mobileMenuOpen && (
          <>
            <motion.div
              key="mobile-nav-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'fixed',
                left: 0,
                right: 0,
                top: mobileDrawerTopPx,
                bottom: 0,
                zIndex: 90,
                background: 'rgba(0,0,0,0.55)',
              }}
              aria-hidden
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.div
              key="mobile-nav-drawer-panel"
              id="nav-mobile-drawer"
              role="dialog"
              aria-modal="true"
              aria-label={t('nav.menu')}
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              style={{
                position: 'fixed',
                left: 0,
                right: 0,
                top: mobileDrawerTopPx,
                zIndex: 95,
                paddingLeft: 'clamp(12px, 4vw, 22px)',
                paddingRight: 'clamp(12px, 4vw, 22px)',
                paddingTop: 14,
                paddingBottom: 'max(18px, env(safe-area-inset-bottom))',
                background: 'rgba(14, 14, 14, 0.97)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                borderBottom: '1px solid rgba(255,255,255,0.08)',
                boxShadow: '0 20px 40px rgba(0,0,0,0.45)',
              }}
            >
              {makeAboutLink({
                fullWidthMobile: true,
                onNavigate: () => setMobileMenuOpen(false),
              })}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
